<template>
  <div class="rte" :class="{ 'rte--focus': focused, 'rte--disabled': disabled }">
    <div class="rte__bar" role="toolbar" :aria-label="t('mailClient.editor.toolbar')" @mousedown="keepSelection">
      <button type="button" class="rte__btn" :title="t('mailClient.editor.undo')" @click="run('undo')"><svg viewBox="0 0 24 24"><path d="M9 14 4 9l5-5M4 9h11a5 5 0 0 1 0 10h-3" /></svg></button>
      <button type="button" class="rte__btn" :title="t('mailClient.editor.redo')" @click="run('redo')"><svg viewBox="0 0 24 24"><path d="m15 14 5-5-5-5M20 9H9a5 5 0 0 0 0 10h3" /></svg></button>
      <span class="rte__sep" />

      <!-- Menus that DO something and go back to their name: the value is never
           kept, so each choice applies again even if it is the same one. -->
      <UiSelect
        class="rte__select"
        model-value=""
        :placeholder="t('mailClient.editor.font')"
        :aria-label="t('mailClient.editor.font')"
        :options="FONTS.map(font => ({ value: font.value, label: font.label }))"
        :searchable="false"
        @update:model-value="run('fontName', $event)"
      />
      <UiSelect
        class="rte__select rte__select--size"
        model-value=""
        :placeholder="t('mailClient.editor.size')"
        :aria-label="t('mailClient.editor.size')"
        :options="SIZES.map(size => ({ value: size.value, label: t(`mailClient.editor.sizes.${size.key}`) }))"
        :searchable="false"
        @update:model-value="run('fontSize', $event)"
      />
      <span class="rte__sep" />

      <button type="button" class="rte__btn" :class="{ on: state.bold }" :title="t('mailClient.editor.bold')" @click="run('bold')"><b>B</b></button>
      <button type="button" class="rte__btn" :class="{ on: state.italic }" :title="t('mailClient.editor.italic')" @click="run('italic')"><i>I</i></button>
      <button type="button" class="rte__btn" :class="{ on: state.underline }" :title="t('mailClient.editor.underline')" @click="run('underline')"><u>U</u></button>
      <button type="button" class="rte__btn" :class="{ on: state.strikeThrough }" :title="t('mailClient.editor.strike')" @click="run('strikeThrough')"><s>S</s></button>

      <div class="rte__pop">
        <button type="button" class="rte__btn" :title="t('mailClient.editor.color')" @click="toggle('color')">
          <span class="rte__a">A</span><span class="rte__swatch" :style="{ background: lastColor }" />
        </button>
        <div v-if="open === 'color'" class="rte__palette">
          <button v-for="color in COLORS" :key="color" type="button" class="rte__chip" :style="{ background: color }" :title="color" @click="pickColor('foreColor', color)" />
          <label class="rte__custom">{{ t('mailClient.editor.other') }} <input type="color" @change="pickColor('foreColor', $event.target.value)"></label>
        </div>
      </div>
      <div class="rte__pop">
        <button type="button" class="rte__btn" :title="t('mailClient.editor.highlight')" @click="toggle('highlight')">
          <svg viewBox="0 0 24 24"><path d="m9 11-6 6v3h9l3-3M22 12l-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4" /></svg><span class="rte__swatch" :style="{ background: lastHighlight }" />
        </button>
        <div v-if="open === 'highlight'" class="rte__palette">
          <button v-for="color in HIGHLIGHTS" :key="color" type="button" class="rte__chip" :style="{ background: color }" :title="color" @click="pickColor('hiliteColor', color)" />
          <button type="button" class="rte__chip rte__chip--none" :title="t('mailClient.editor.none')" @click="pickColor('hiliteColor', 'transparent')">∅</button>
        </div>
      </div>
      <span class="rte__sep" />

      <button type="button" class="rte__btn" :class="{ on: state.insertUnorderedList }" :title="t('mailClient.editor.bullets')" @click="run('insertUnorderedList')"><svg viewBox="0 0 24 24"><path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" /></svg></button>
      <button type="button" class="rte__btn" :class="{ on: state.insertOrderedList }" :title="t('mailClient.editor.numbers')" @click="run('insertOrderedList')"><svg viewBox="0 0 24 24"><path d="M10 6h10M10 12h10M10 18h10M4 6h1v4M4 10h2M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" /></svg></button>
      <button type="button" class="rte__btn" :title="t('mailClient.editor.outdent')" @click="run('outdent')"><svg viewBox="0 0 24 24"><path d="M21 6H11M21 12h-8M21 18H11M7 8l-4 4 4 4" /></svg></button>
      <button type="button" class="rte__btn" :title="t('mailClient.editor.indent')" @click="run('indent')"><svg viewBox="0 0 24 24"><path d="M21 6H11M21 12h-8M21 18H11M3 8l4 4-4 4" /></svg></button>
      <span class="rte__sep" />

      <button type="button" class="rte__btn" :class="{ on: state.justifyLeft }" :title="t('mailClient.editor.left')" @click="run('justifyLeft')"><svg viewBox="0 0 24 24"><path d="M3 6h18M3 12h12M3 18h16" /></svg></button>
      <button type="button" class="rte__btn" :class="{ on: state.justifyCenter }" :title="t('mailClient.editor.center')" @click="run('justifyCenter')"><svg viewBox="0 0 24 24"><path d="M3 6h18M6 12h12M4 18h16" /></svg></button>
      <button type="button" class="rte__btn" :class="{ on: state.justifyRight }" :title="t('mailClient.editor.right')" @click="run('justifyRight')"><svg viewBox="0 0 24 24"><path d="M3 6h18M9 12h12M5 18h16" /></svg></button>
      <button type="button" class="rte__btn" :class="{ on: state.justifyFull }" :title="t('mailClient.editor.justify')" @click="run('justifyFull')"><svg viewBox="0 0 24 24"><path d="M3 6h18M3 12h18M3 18h18" /></svg></button>
      <span class="rte__sep" />

      <div class="rte__pop">
        <button type="button" class="rte__btn" :title="t('mailClient.editor.link')" @click="openLink"><svg viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" /></svg></button>
        <form v-if="open === 'link'" class="rte__palette rte__link" @submit.prevent="applyLink">
          <input ref="linkInput" v-model="linkUrl" type="url" autocapitalize="off" autocorrect="off" spellcheck="false" enterkeyhint="done" :placeholder="t('mailClient.editor.linkPlaceholder')" @mousedown.stop>
          <button type="submit" class="rte__ok">{{ t('mailClient.editor.apply') }}</button>
          <button type="button" class="rte__ok" @click="unlink">{{ t('mailClient.editor.unlink') }}</button>
        </form>
      </div>
      <button type="button" class="rte__btn" :title="t('mailClient.editor.quote')" @click="run('formatBlock', 'blockquote')"><svg viewBox="0 0 24 24"><path d="M3 21c3 0 7-1 7-8V5H3v7h4M14 21c3 0 7-1 7-8V5h-7v7h4" /></svg></button>
      <button type="button" class="rte__btn" :title="t('mailClient.editor.clear')" @click="run('removeFormat')"><svg viewBox="0 0 24 24"><path d="M4 7V4h16v3M9 20h6M12 4v16M3 3l18 18" /></svg></button>
    </div>

    <div
      ref="area"
      class="rte__area"
      :contenteditable="!disabled"
      role="textbox"
      aria-multiline="true"
      :aria-label="label || t('mailClient.editor.body')"
      :data-placeholder="placeholder"
      :style="{ minHeight }"
      @input="emitValue"
      @focus="focused = true"
      @blur="focused = false; emitValue()"
      @paste="onPaste"
      @drop="onDrop"
      @keyup="readState"
      @mouseup="readState"
      @keydown="onKey"
    />
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiSelect } from '@general/ui-kit'

import { sanitizeHtml, textToHtml } from './sanitize'

/**
 * The composer's editor: formatted text, the way an email client writes it.
 *
 * Built on the browser's own editing (`contenteditable` + `execCommand`) and no
 * editor library: everything an email needs — bold, lists, colours, alignment,
 * links, pasting from Word or a web page — the browser already does, and the
 * result is plain HTML that any mail client reads. `execCommand` is marked
 * obsolete but every browser keeps it, precisely because editors like this
 * depend on it.
 *
 * What comes in is cleaned (sanitize.js) before it touches the page: a paste,
 * a draft, what the AI wrote.
 */
const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  label: { type: String, default: '' },
  disabled: Boolean,
  minHeight: { type: String, default: '220px' },
})
const emit = defineEmits(['update:modelValue', 'files'])
const { t } = useI18n()

const FONTS = [
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
]
const SIZES = [
  { key: 'small', value: '2' },
  { key: 'normal', value: '3' },
  { key: 'large', value: '5' },
  { key: 'huge', value: '6' },
]
const COLORS = ['#1f2328', '#57606a', '#b42318', '#c2410c', '#a16207', '#15803d', '#0f766e', '#1d4ed8', '#6d28d9', '#be185d']
const HIGHLIGHTS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#fed7aa', '#e5e7eb']

const area = ref(null)
const linkInput = ref(null)
const focused = ref(false)
const open = ref('')
const linkUrl = ref('')
const lastColor = ref('#b42318')
const lastHighlight = ref('#fef08a')
const state = reactive({})
let saved = null
let lastEmitted = null

onMounted(() => {
  setHtml(props.modelValue)
  document.addEventListener('mousedown', closePopovers)
})
onBeforeUnmount(() => document.removeEventListener('mousedown', closePopovers))

watch(() => props.modelValue, (value) => {
  if (value !== lastEmitted) setHtml(value)
})

function setHtml(html) {
  if (!area.value) return
  const clean = sanitizeHtml(html || '')
  area.value.innerHTML = clean
  lastEmitted = html
}

function emitValue() {
  if (!area.value) return
  const html = area.value.innerHTML === '<br>' ? '' : area.value.innerHTML
  lastEmitted = html
  emit('update:modelValue', html)
}

// ── Commands ─────────────────────────────────────────────────────────────────

function keepSelection(event) {
  // Clicking the toolbar must not take the caret out of the text.
  if (event.target.closest('select, input')) { rememberRange(); return }
  event.preventDefault()
  rememberRange()
}

function rememberRange() {
  const selection = window.getSelection()
  if (selection?.rangeCount && area.value?.contains(selection.anchorNode)) saved = selection.getRangeAt(0).cloneRange()
}

function restoreRange() {
  area.value?.focus()
  if (!saved) return
  const selection = window.getSelection()
  selection.removeAllRanges()
  selection.addRange(saved)
}

function run(command, value = null) {
  if (props.disabled) return
  restoreRange()
  document.execCommand('styleWithCSS', false, command === 'fontSize' ? false : true)
  document.execCommand(command, false, value)
  readState()
  emitValue()
}

function readState() {
  rememberRange()
  for (const command of ['bold', 'italic', 'underline', 'strikeThrough', 'insertUnorderedList', 'insertOrderedList', 'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull']) {
    try { state[command] = document.queryCommandState(command) } catch { state[command] = false }
  }
}

function toggle(name) {
  open.value = open.value === name ? '' : name
}

function closePopovers(event) {
  if (!event.target.closest?.('.rte__pop')) open.value = ''
}

function pickColor(command, color) {
  if (command === 'foreColor') lastColor.value = color
  else lastHighlight.value = color
  open.value = ''
  run(command, color)
}

async function openLink() {
  rememberRange()
  const anchor = window.getSelection()?.anchorNode?.parentElement?.closest?.('a')
  linkUrl.value = anchor?.getAttribute('href') ?? ''
  open.value = open.value === 'link' ? '' : 'link'
  await nextTick()
  linkInput.value?.focus()
}

function applyLink() {
  let url = linkUrl.value.trim()
  if (!url) { open.value = ''; return }
  if (!/^(https?:|mailto:|tel:)/i.test(url)) url = url.includes('@') && !url.includes('/') ? `mailto:${url}` : `https://${url}`
  open.value = ''
  restoreRange()
  const selection = window.getSelection()
  if (selection && selection.isCollapsed) {
    document.execCommand('insertHTML', false, `<a href="${url.replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer">${url.replace(/</g, '&lt;')}</a>`)
  } else {
    document.execCommand('createLink', false, url)
  }
  emitValue()
}

function unlink() {
  open.value = ''
  run('unlink')
}

function onKey(event) {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    openLink()
  }
}

// ── What comes in from outside ───────────────────────────────────────────────

function onPaste(event) {
  const data = event.clipboardData
  if (!data) return
  if (data.files?.length) {
    event.preventDefault()
    emit('files', [...data.files])
    return
  }
  const html = data.getData('text/html')
  const text = data.getData('text/plain')
  event.preventDefault()
  document.execCommand('insertHTML', false, html ? sanitizeHtml(html) : textToHtml(text))
  emitValue()
}

function onDrop(event) {
  if (event.dataTransfer?.files?.length) {
    event.preventDefault()
    emit('files', [...event.dataTransfer.files])
  }
}

/** For the composer: put HTML where the caret is (or at the end). */
function insertHtml(html) {
  restoreRange()
  document.execCommand('insertHTML', false, sanitizeHtml(html))
  emitValue()
}

function focus() {
  area.value?.focus()
}

defineExpose({ insertHtml, focus, setHtml: (html) => { setHtml(html); emitValue() } })
</script>

<style scoped>
.rte {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  min-height: 0;
}
.rte--focus { border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-primary-ring); }
.rte--disabled { opacity: .7; }

.rte__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface-2);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}
.rte__btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 28px;
  height: 28px;
  padding: 0 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text);
  font-size: 14px;
  cursor: pointer;
}
.rte__btn:hover { background: var(--color-gray-100); }
.rte__btn.on { background: var(--color-primary-soft); color: var(--color-primary); }
.rte__btn svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.rte__a { font-weight: 700; }
.rte__swatch { width: 12px; height: 4px; border-radius: 2px; position: absolute; bottom: 3px; left: 50%; transform: translateX(-50%); }
.rte__sep { width: 1px; height: 18px; margin: 0 4px; background: var(--color-border); }
/* The kit's dropdown at the toolbar's height. */
.rte__select { width: 104px; }
.rte__select--size { width: 84px; }
.rte__select :deep(.select__trigger) { height: 28px; padding: 0 6px; font-size: var(--text-xs); }
.rte__pop { position: relative; }
.rte__palette {
  position: absolute;
  z-index: 30;
  top: calc(100% + 4px);
  left: 0;
  display: grid;
  grid-template-columns: repeat(5, 22px);
  gap: 6px;
  padding: 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}
.rte__chip { width: 22px; height: 22px; border-radius: 50%; border: 1px solid var(--color-border); cursor: pointer; padding: 0; }
.rte__chip--none { background: var(--color-surface); font-size: 12px; color: var(--color-text-soft); }
.rte__custom { grid-column: 1 / -1; display: flex; align-items: center; justify-content: space-between; font-size: var(--text-xs); color: var(--color-text-soft); }
.rte__custom input { width: 32px; height: 22px; border: 0; padding: 0; background: none; }
.rte__link { display: flex; grid-template-columns: none; width: 320px; }
.rte__link input { flex: 1; min-width: 0; height: 28px; border: 1px solid var(--color-border); border-radius: 6px; padding: 0 8px; background: var(--color-surface); color: var(--color-text); }
.rte__ok { height: 28px; border: 0; border-radius: 6px; padding: 0 8px; background: var(--color-primary-soft); color: var(--color-primary); cursor: pointer; font-size: var(--text-xs); white-space: nowrap; }

.rte__area {
  flex: 1;
  padding: var(--space-3);
  overflow-y: auto;
  outline: none;
  font: 14px/1.5 Arial, Helvetica, sans-serif;
  color: var(--color-text);
  word-wrap: break-word;
}
.rte__area:empty::before { content: attr(data-placeholder); color: var(--color-text-mute); pointer-events: none; }
.rte__area :deep(blockquote) { margin: 8px 0; padding-left: 12px; border-left: 3px solid var(--color-border); color: var(--color-text-soft); }
.rte__area :deep(a) { color: var(--color-primary); }
.rte__area :deep(img) { max-width: 100%; }
.rte__area :deep(ul), .rte__area :deep(ol) { padding-left: 24px; }

@media (max-width: 640px) {
  .rte__select { width: 96px; }
}

/* En el teléfono la barra son tres renglones de botones (casi 140 px) encima
   del texto, y con el teclado fuera no quedaba sitio para escribir: un solo
   renglón que se desliza con el dedo, como la barra de formato de Gmail.
   Las paletas (color, resaltado, enlace) se colocan contra el editor y no
   contra su botón: dentro de una barra con scroll, la barra las recortaba (un
   `overflow` solo recorta lo que tiene su caja de referencia dentro). */
@media (max-width: 760px) {
  .rte { position: relative; }
  .rte__bar { flex-wrap: nowrap; overflow-x: auto; overscroll-behavior-x: contain; scrollbar-width: none; }
  .rte__bar::-webkit-scrollbar { display: none; }
  .rte__bar > * { flex-shrink: 0; }
  /* Lo que más se usa al principio del renglón (negrita, listas, enlace) y la
     tipografía y el tamaño al final: en el escritorio van primero porque se
     ven todos a la vez; aquí, lo primero es lo único que se ve sin deslizar. */
  .rte__select { order: 1; }
  /* Los separadores agrupaban por renglones; en uno solo que se desliza, con
     las listas movidas al final, quedaban dos seguidos. */
  .rte__sep { display: none; }
  .rte__pop { position: static; }
  .rte__palette { top: 52px; left: 8px; }
  .rte__link { left: 8px; right: 8px; width: auto; }
}

/* Con el dedo: botones de 40 px (en un renglón que se desliza caben), el texto
   a 16 px (con menos, iOS amplía la página al tocar el editor y no la
   devuelve; es solo cómo se ve aquí, el correo no lleva ese tamaño) y la
   caja del enlace igual. */
@media (pointer: coarse) {
  .rte__btn { min-width: 40px; height: 40px; }
  .rte__select :deep(.select__trigger) { height: 40px; }
  .rte__palette { grid-template-columns: repeat(5, 32px); gap: 10px; }
  .rte__chip { width: 32px; height: 32px; }
  .rte__link input { height: 40px; font-size: var(--text-field, 16px); }
  .rte__ok { height: 40px; padding: 0 12px; }
  .rte__area { font-size: var(--text-field, 16px); }
}
</style>
